import {
  BookOpen,
  ClockArrowRotateLeft,
  Comment,
  DisplayPulse,
  Gear,
  Heart,
  Hierarchy,
  House,
  ListCheck,
  Person,
  Persons,
  Pulse,
  ShoppingCart,
  Star,
  Wrench,
} from '@gravity-ui/icons';
import type { ReactElement } from 'react';
import type { User } from '@/api/model';
import {
  hasRole,
  ROLE_HEALTH_MGR,
  ROLE_OPERATOR,
  ROLE_SPEC_DOCTOR,
  ROLE_SUPER_ADMIN,
} from '@/lib/role';

// 菜单项：取代后端 RBAC 菜单树，直接在前端按角色硬编码
export interface MenuItem {
  // 唯一 key；叶子节点即路由路径，分组节点用 group-* 占位
  key: string;
  // 显示名称
  name: string;
  // 路由路径；分组节点无
  path?: string;
  // 图标
  icon?: ReactElement;
  // 可见 / 可访问该项的角色 id（对应 ROLE_* 常量）
  roles: string[];
  // 子菜单
  children?: MenuItem[];
  // 仅放行路由、不在侧边栏展示（如健康方案，从患者详情进入）
  hiddenInSidebar?: boolean;
}

// 四类角色全部可见
const ALL_ROLES = [
  ROLE_SUPER_ADMIN,
  ROLE_HEALTH_MGR,
  ROLE_SPEC_DOCTOR,
  ROLE_OPERATOR,
];

// 全部菜单，按侧边栏展示顺序声明（角色映射与历史 011_seed_rbac.sql 保持一致）
export const MENU_ITEMS: MenuItem[] = [
  {
    key: '/console/dashboard',
    name: '控制台',
    path: '/console/dashboard',
    icon: <House className="size-4" />,
    roles: ALL_ROLES,
  },
  {
    key: '/console/institution-agent',
    name: 'AI巡检',
    path: '/console/institution-agent',
    icon: <DisplayPulse className="size-4" />,
    roles: ALL_ROLES,
  },
  {
    key: '/console/patient',
    name: '平台患者池',
    path: '/console/patient',
    icon: <Persons className="size-4" />,
    roles: ALL_ROLES,
  },
  {
    key: '/console/patient/my',
    name: '我的患者',
    path: '/console/patient/my',
    icon: <Star className="size-4" />,
    roles: [ROLE_HEALTH_MGR, ROLE_SPEC_DOCTOR],
  },
  {
    key: '/console/health-plan',
    name: '健康方案',
    path: '/console/health-plan',
    icon: <Heart className="size-4" />,
    roles: ALL_ROLES,
    hiddenInSidebar: true,
  },
  {
    key: '/console/product',
    name: '产品库',
    path: '/console/product',
    icon: <ShoppingCart className="size-4" />,
    roles: [ROLE_SUPER_ADMIN, ROLE_OPERATOR],
  },
  {
    key: '/console/health-education',
    name: '健康科普',
    path: '/console/health-education',
    icon: <BookOpen className="size-4" />,
    roles: [ROLE_SUPER_ADMIN, ROLE_OPERATOR],
  },
  {
    key: '/console/exercise-guide',
    name: '运动指导',
    path: '/console/exercise-guide',
    icon: <Pulse className="size-4" />,
    roles: [ROLE_SUPER_ADMIN, ROLE_OPERATOR],
  },
  {
    key: 'group-system',
    name: '系统管理',
    icon: <Gear className="size-4" />,
    roles: [ROLE_SUPER_ADMIN],
    children: [
      {
        key: '/console/system/department',
        name: '科室管理',
        path: '/console/system/department',
        icon: <Hierarchy className="size-4" />,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        key: '/console/system/account',
        name: '账户管理',
        path: '/console/system/account',
        icon: <Person className="size-4" />,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        key: '/console/system/scale',
        name: '量表维护',
        path: '/console/system/scale',
        icon: <ListCheck className="size-4" />,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        key: '/console/system/follow-up',
        name: '随访维护',
        path: '/console/system/follow-up',
        icon: <ClockArrowRotateLeft className="size-4" />,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        key: '/console/system/wework-kf-account',
        name: '微信客服',
        path: '/console/system/wework-kf-account',
        icon: <Comment className="size-4" />,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        key: '/console/system/settings',
        name: '系统设置',
        path: '/console/system/settings',
        icon: <Wrench className="size-4" />,
        roles: [ROLE_SUPER_ADMIN],
      },
    ],
  },
];

type MaybeUser = User | undefined | null;

const canSee = (user: MaybeUser, item: MenuItem): boolean =>
  hasRole(user, ...item.roles);

// 始终放行的路径：根、控制台、错误页等无需角色授权即可进入
const ALWAYS_ALLOWED = new Set([
  '/console',
  '/console/dashboard',
  '/console/403',
  '/console/no-permission',
]);

// 侧边栏可见菜单：按角色过滤、剔除 hiddenInSidebar、丢弃空分组
export function visibleMenus(user: MaybeUser): MenuItem[] {
  const walk = (items: MenuItem[]): MenuItem[] =>
    items
      .filter((it) => canSee(user, it) && !it.hiddenInSidebar)
      .map((it) => (it.children ? { ...it, children: walk(it.children) } : it))
      .filter((it) => it.path != null || (it.children?.length ?? 0) > 0);
  return walk(MENU_ITEMS);
}

// 当前用户可访问的全部路径（含 hiddenInSidebar），用于路由守卫
export function accessiblePaths(user: MaybeUser): Set<string> {
  const paths = new Set<string>();
  const walk = (items: MenuItem[]) => {
    for (const it of items) {
      if (canSee(user, it) && it.path) paths.add(it.path);
      if (it.children) walk(it.children);
    }
  };
  walk(MENU_ITEMS);
  return paths;
}

// 路径是否允许访问：命中白名单 / 精确菜单 / 菜单的子路由（详情、编辑页）
export function isPathAllowed(user: MaybeUser, current: string): boolean {
  if (ALWAYS_ALLOWED.has(current)) return true;
  const allowed = accessiblePaths(user);
  if (allowed.has(current)) return true;
  // 详情 / 编辑等子路由：/console/patient/x、/console/health-plan/x、/console/health-plan/edit/x
  for (const p of allowed) {
    if (current.startsWith(`${p}/`)) return true;
  }
  return false;
}

// 是否完全没有任何可访问菜单（未分配已知角色）
export function hasNoAccess(user: MaybeUser): boolean {
  return accessiblePaths(user).size === 0;
}

// 首个可见菜单路径，用于根路径重定向
export function firstVisiblePath(user: MaybeUser): string | null {
  const find = (items: MenuItem[]): string | null => {
    for (const it of items) {
      if (!canSee(user, it) || it.hiddenInSidebar) continue;
      if (it.path) return it.path;
      if (it.children) {
        const child = find(it.children);
        if (child) return child;
      }
    }
    return null;
  };
  return find(MENU_ITEMS);
}

// 展平所有带 path 的菜单项（含子项）
function flatItems(items: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = [];
  for (const it of items) {
    if (it.path) out.push(it);
    if (it.children) out.push(...flatItems(it.children));
  }
  return out;
}

// 按路径反查菜单标题（最长前缀匹配），用于页头标题
export function findMenuTitle(current: string): string | null {
  let best: { path: string; name: string } | null = null;
  for (const it of flatItems(MENU_ITEMS)) {
    const path = it.path;
    if (path == null) continue;
    if (current === path || current.startsWith(`${path}/`)) {
      if (!best || path.length > best.path.length) {
        best = { path, name: it.name };
      }
    }
  }
  return best ? best.name : null;
}
